/* eslint react-hooks/exhaustive-deps: "warn" */

import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './WhereItWorksSection.module.scss';

type Translate = (key: string) => string;

type SurfaceRow = {
  term: string;
  desc: string;
};

type Surface = {
  n: string;
  label: string;
  title: string;
  context: string;
  rows: SurfaceRow[];
  icon: 'globe' | 'linkedin';
};

const ICON_TYPE: Record<Surface['icon'], string> = {
  globe: 'global',
  linkedin: 'media-2',
};

const getSurfaces = (t: Translate): Surface[] => [
  {
    n: '01',
    label: t('Any website'),
    icon: 'globe',
    title: t('Any website'),
    context: t(
      'The extension resolves the company named on the page to its registry record and opens the panel beside it.',
    ),
    rows: [
      {
        term: t('Disambiguation'),
        desc: t(
          'Where several registered entities share a name, candidates are listed with jurisdiction and registration number before you save.',
        ),
      },
      {
        term: t('Match signals'),
        desc: t('Resolved from the domain, the legal name and the registration number, in that order of precedence.'),
      },
      {
        term: t('No match'),
        desc: t('Search by name or registration number from the panel rather than leaving the page.'),
      },
    ],
  },
  {
    n: '02',
    label: 'LinkedIn',
    icon: 'linkedin',
    title: 'LinkedIn',
    context: t('The panel returns the registered entity behind the brand, and the employer record behind the person.'),
    rows: [
      {
        term: t('Company page'),
        desc: t('Resolves the page to the legal entity that files, rather than the brand that markets.'),
      },
      {
        term: t('Person profile'),
        desc: t("Returns the employer's filed record alongside the person whose profile is open."),
      },
      {
        term: t('Group'),
        desc: t('Surfaces the parent and the subsidiaries, so you see who ultimately owns the entity.'),
      },
      {
        term: t('Capture'),
        desc: t('Adds the company and the executive to your CRM or a list in one action.'),
      },
    ],
  },
];

export const WhereItWorksSection = () => {
  const { t } = useInt();
  const [active, setActive] = useState(0);

  const surfaces = getSurfaces(t);
  const surface = surfaces[active];

  return (
    <Section className={styles.root}>
      <div className={styles.gridPattern} aria-hidden="true" />
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.top}>
        <TitleBlock theme="dark" title={t('Two surfaces, one record.')} />

        <div className={styles.seg} role="tablist" aria-label={t('Surfaces')}>
          <i className={styles.segIndicator} style={{ transform: `translateX(${active * 100}%)` }} />

          {surfaces.map((item, index) => (
            <button
              key={item.label}
              type="button"
              role="tab"
              aria-selected={active === index}
              className={cn(styles.segBtn, active === index && styles.segBtnActive)}
              onClick={() => setActive(index)}
            >
              <span className={styles.tabIcon}>
                <Icon type={ICON_TYPE[item.icon]} />
              </span>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div key={active} className={styles.card}>
        <div className={styles.side}>
          <span className={styles.ghost} aria-hidden="true">
            {surface.n}
          </span>

          <div className={styles.sideContent}>
            <p className={styles.n}>{surface.n}</p>
            <h3 className={styles.title}>{surface.title}</h3>
            <p className={styles.context}>{surface.context}</p>
          </div>
        </div>

        <dl className={styles.rows}>
          {surface.rows.map((row, index) => (
            <div key={row.term} style={{ animationDelay: `${0.32 + index * 0.06}s` }}>
              <dt>{row.term}</dt>
              <dd>{row.desc}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Section>
  );
};
